import { IDS_ECOA_COLUMNS, IdsEcoaRow, ValidationError } from './types';

export const EXPECTED_HEADERS = IDS_ECOA_COLUMNS.map((column) => column.label);
export const REQUIRED_IMPORT_HEADERS = ['ID Regra', 'Nome Promoção', 'Mês Referência'];

const MONTH_LABELS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export const MONTH_OPTIONS = MONTH_LABELS.map((label, index) => ({
  value: String(index + 1),
  label,
}));

const MONTH_ALIASES = new Map<string, number>([
  ['jan', 1],
  ['janeiro', 1],
  ['fev', 2],
  ['fevereiro', 2],
  ['mar', 3],
  ['marco', 3],
  ['março', 3],
  ['abr', 4],
  ['abril', 4],
  ['mai', 5],
  ['maio', 5],
  ['jun', 6],
  ['junho', 6],
  ['jul', 7],
  ['julho', 7],
  ['ago', 8],
  ['agosto', 8],
  ['set', 9],
  ['setembro', 9],
  ['out', 10],
  ['outubro', 10],
  ['nov', 11],
  ['novembro', 11],
  ['dez', 12],
  ['dezembro', 12],
]);

export function makeLocalGuid() {
  return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function createEmptyRow(): IdsEcoaRow {
  return {
    guid: makeLocalGuid(),
    idRegra: '',
    nomePromocao: '',
    mesReferencia: '',
    anoReferencia: String(new Date().getFullYear()),
  };
}

export function normalizeText(value: any) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

export function normalizeKey(value: any) {
  return normalizeText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export function normalizeHeaderKey(value: any) {
  return normalizeKey(value).replace(/[^a-z0-9]/g, '');
}

export function parseMonthNumber(value: any) {
  const text = normalizeText(value);
  if (!text) return null;

  const numericValue = Number(text.replace(',', '.'));
  if (Number.isInteger(numericValue) && numericValue >= 1 && numericValue <= 12) {
    return numericValue;
  }

  return MONTH_ALIASES.get(normalizeKey(text)) || null;
}

export function finalizeMonthValue(value: any) {
  const month = parseMonthNumber(value);
  return month === null ? normalizeText(value) : String(month);
}

export function formatMonthLabel(value: any) {
  const month = parseMonthNumber(value);
  return month === null ? normalizeText(value) : MONTH_LABELS[month - 1];
}

export function getMonthSelectValue(value: any) {
  const month = parseMonthNumber(value);
  return month === null ? '' : String(month);
}

export function getCurrentYear() {
  return new Date().getFullYear();
}

export function parseYearNumber(value: any) {
  const text = normalizeText(value);
  if (!text) return null;
  const year = Number(text);
  if (!Number.isInteger(year) || year < 2020 || year > 2200) return null;
  return year;
}

export function finalizeYearValue(value: any) {
  const year = parseYearNumber(value);
  return year === null ? String(getCurrentYear()) : String(year);
}

export function getYearSelectValue(value: any) {
  const year = parseYearNumber(value);
  return year === null ? '' : String(year);
}

export function getYearOptions(baseYear = getCurrentYear()) {
  return Array.from({ length: Math.max(1, baseYear - 2020 + 1) }, (_, index) => baseYear - index);
}

export function prepareRowsForSave(rows: IdsEcoaRow[]) {
  return rows
    .map((row) => ({
      ...row,
      idRegra: normalizeText(row.idRegra),
      nomePromocao: normalizeText(row.nomePromocao),
      mesReferencia: finalizeMonthValue(row.mesReferencia) || null,
      anoReferencia: finalizeYearValue(row.anoReferencia),
    }))
    .filter((row) => !isBlankRow(row));
}

export function isBlankRow(row: IdsEcoaRow) {
  return IDS_ECOA_COLUMNS.every((column) => {
    const value = row[column.key];
    return value === null || value === undefined || String(value).trim() === '';
  });
}

export function rowsAreEqual(a: IdsEcoaRow, b: IdsEcoaRow) {
  return IDS_ECOA_COLUMNS.every((column) => {
    const left = column.type === 'month'
      ? finalizeMonthValue(a[column.key])
      : column.type === 'year'
        ? finalizeYearValue(a[column.key])
        : normalizeText(a[column.key]);
    const right = column.type === 'month'
      ? finalizeMonthValue(b[column.key])
      : column.type === 'year'
        ? finalizeYearValue(b[column.key])
        : normalizeText(b[column.key]);
    return left === right;
  });
}

export function getChangedRows(rows: IdsEcoaRow[], originalRows: IdsEcoaRow[]) {
  const originalByGuid = new Map(originalRows.map((row) => [row.guid, row]));

  return rows.filter((row) => {
    if (isBlankRow(row)) return false;
    const original = originalByGuid.get(row.guid);
    return !original || !rowsAreEqual(row, original);
  });
}

export function getRowIdentityKey(row: IdsEcoaRow) {
  return [
    normalizeKey(row.idRegra),
    parseMonthNumber(row.mesReferencia) || '',
    parseYearNumber(row.anoReferencia) || getCurrentYear(),
  ].join('|');
}

export function mergeRowsByIdentity(currentRows: IdsEcoaRow[], importedRows: IdsEcoaRow[]) {
  const nextRows = [...currentRows];
  const indexByIdentity = new Map<string, number>();

  nextRows.forEach((row, index) => {
    if (row.idRegra) indexByIdentity.set(getRowIdentityKey(row), index);
  });

  importedRows.forEach((row) => {
    const key = getRowIdentityKey(row);
    const index = indexByIdentity.get(key);

    if (index === undefined) {
      nextRows.push(row);
      indexByIdentity.set(key, nextRows.length - 1);
    } else {
      nextRows[index] = {
        ...row,
        guid: nextRows[index].guid,
      };
    }
  });

  return nextRows;
}

export function validateRows(rows: IdsEcoaRow[]) {
  const errors: ValidationError[] = [];
  const seen = new Map<string, number>();

  rows.forEach((row, index) => {
    if (isBlankRow(row)) return;

    const rowNumber = index + 1;
    const month = parseMonthNumber(row.mesReferencia);
    const year = parseYearNumber(row.anoReferencia) || getCurrentYear();

    if (!normalizeText(row.idRegra)) {
      errors.push({ row: rowNumber, field: 'idRegra', message: 'ID Regra é obrigatório' });
    }

    if (!normalizeText(row.nomePromocao)) {
      errors.push({ row: rowNumber, field: 'nomePromocao', message: 'Nome Promoção é obrigatório' });
    }

    if (!month) {
      errors.push({ row: rowNumber, field: 'mesReferencia', message: 'Mês Referência é obrigatório' });
    }

    if (normalizeText(row.anoReferencia) && !parseYearNumber(row.anoReferencia)) {
      errors.push({ row: rowNumber, field: 'anoReferencia', message: 'Ano Referência deve ser a partir de 2020' });
    }

    if (!row.idRegra || !month) return;

    const key = [normalizeKey(row.idRegra), month, year].join('|');
    if (seen.has(key)) {
      errors.push({
        row: rowNumber,
        field: 'idRegra',
        message: `ID Regra duplicado para o mesmo mês/ano. Já aparece na linha ${seen.get(key)}`,
      });
    } else {
      seen.set(key, rowNumber);
    }
  });

  return errors;
}

export function validationErrorLines(errors: ValidationError[] = []) {
  return errors.map((error) => {
    const row = error.row > 0 ? `Linha ${error.row}` : 'Tabela';
    return `${row}: ${error.message}`;
  });
}
